<template>
  <section class="mx-auto flex min-h-[70vh] max-w-3xl flex-col gap-8 px-6 py-10">

    <router-link
      to="/tournament"
      class="block overflow-hidden rounded-[2rem] border border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(245,158,11,0.22),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(34,197,94,0.16),_transparent_30%),rgba(15,23,42,0.92)] p-6 transition hover:border-white/20"
    >
      <div class="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div class="max-w-2xl">
          <p class="text-xs uppercase tracking-[0.35em] text-amber-200/80">New Arena</p>
          <h2 class="mt-2 font-display text-3xl font-semibold text-white">Tournament rooms are live</h2>
          <p class="mt-3 text-sm leading-6 text-steel-200">
            Join one of 8 opening rooms, post your car photo and specs, then let the community vote each matchup down to a single champion.
          </p>
        </div>
        <span class="inline-flex items-center justify-center rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900">
          Open bracket
        </span>
      </div>
    </router-link>

    <!-- Top 5 users by likes (click opens profile). Scrollable on small devices. -->
    <div class="mt-1">
      <div class="flex items-center justify-between">
        <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Top Members</p>
        <p v-if="topUsersLoading" class="text-xs text-steel-400">Loading...</p>
      </div>

      <div class="mt-3 -mx-4 overflow-x-auto px-4 no-scrollbar">
        <div class="flex gap-4">
          <router-link
            v-for="user in topUsers"
            :key="user.id"
            :to="`/u/${user.handle}`"
            class="flex-shrink-0 w-44 rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 shadow hover:border-white/20"
          >
            <div class="flex items-center gap-3">
              <div class="relative">
                <div class="h-12 w-12 overflow-hidden rounded-full border-2 border-white/10 bg-midnight-900/70">
                  <img
                    v-if="user.avatar"
                    :src="user.avatar"
                    alt=""
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                  <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ user.initials }}</div>
                </div>
                <div
                  class="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold"
                  :class="user.rank === 1 ? 'bg-yellow-400 text-black' : user.rank === 2 ? 'bg-slate-300 text-black' : user.rank === 3 ? 'bg-amber-700 text-white' : 'bg-white/5 text-white'"
                >
                  {{ user.rank }}
                </div>
              </div>
              <div class="min-w-0">
                <p class="truncate text-sm text-white">{{ user.fullName }}</p>
                <p class="text-xs text-steel-400">@{{ user.handle }}</p>
                <p class="mt-1 text-xs text-steel-400">{{ user.likeCount }} likes</p>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div class="flex flex-col gap-2">
        <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Feed</p>
        <h1 class="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
          The DRVN wall
        </h1>
        <p class="text-base text-steel-200/90">
          Member moments, builds, and the long way home.
        </p>
      </div>
      <div class="flex flex-wrap gap-3 sm:justify-end">
        <button
          type="button"
          class="rounded-2xl border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] transition"
          :class="activeFeedFilter === 'feed' ? 'bg-white text-midnight-900' : 'bg-white/5 text-white hover:border-white/30'"
          @click="activeFeedFilter = 'feed'"
        >
          Feed
        </button>
        <button
          type="button"
          class="rounded-2xl border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] transition"
          :class="activeFeedFilter === 'selling' ? 'bg-white text-midnight-900' : 'bg-white/5 text-white hover:border-white/30'"
          @click="activeFeedFilter = 'selling'"
        >
          For Sale
        </button>
      </div>
    </div>

    <div class="grid gap-3">
      <router-link
        v-for="post in filteredPosts"
        :key="post.id"
        :to="`/post/${post.id}`"
        class="block bg-midnight-800/60 shadow-glow transition hover:border-white/20 rounded-2xl overflow-hidden"
      >
        <!-- Header (avatar, name, menu) -->
        <div class="flex items-center gap-3 px-4 py-3">
          <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
            <img
              v-if="post.authorAvatar"
              :src="post.authorAvatar"
              alt="Author avatar"
              class="h-full w-full object-cover"
              referrerpolicy="no-referrer"
              crossorigin="anonymous"
            />
            <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ post.authorInitials }}</div>
          </div>
          <div class="min-w-0">
            <p class="text-sm font-semibold text-white">{{ post.authorName }}</p>
            <router-link class="text-xs text-steel-400 hover:text-white" :to="`/u/${post.authorHandle}`">@{{ post.authorHandle }}</router-link>
          </div>
          <button
            type="button"
            class="ml-auto text-steel-400 hover:text-white"
            @click.stop.prevent="openReportModal(post)"
          >
            ⋯
          </button>
        </div>

        <!-- Image (consistent aspect ratio) -->
        <div
          class="relative aspect-square w-full bg-black"
          style="touch-action: pan-y;"
          @touchstart="handleSwipeStart($event)"
          @touchend="handleSwipeEnd($event, post)"
        >
          <transition :name="post.mediaDirection === 'prev' ? 'media-swipe-prev' : 'media-swipe-next'" mode="out-in">
            <img
              :key="currentMedia(post)"
              :src="currentMedia(post)"
              alt="Post"
              class="absolute inset-0 h-full w-full object-cover"
              :style="{ objectPosition: currentMediaPosition(post) }"
            />
          </transition>

          <div v-if="post.mediaUrls.length > 1" class="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 z-10 pointer-events-none">
            <button
              v-for="(url, idx) in post.mediaUrls"
              :key="idx"
              type="button"
              class="h-1 rounded-full transition-all pointer-events-auto"
              :class="idx === post.mediaIndex ? 'w-6 bg-white' : 'w-1 bg-white/40'"
              @click="setMediaIndex(post, idx)"
            />
          </div>
        </div>

        <!-- Actions and meta -->
        <div class="px-4 py-3">
          <div class="flex items-center gap-3">
            <button type="button" class="inline-flex items-center gap-2" :class="post.isLiked ? 'text-rose-500' : 'text-white'" @click.stop.prevent="toggleLike(post)">
              <svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 21s-6.7-4.3-9.3-7.6C.5 10.8 1 7.6 3 5.7c2-2 5.2-1.6 7 0l2 1.9 2-1.9c1.8-1.6 5-2 7 0 2 1.9 2.5 5.1.3 7.7C18.7 16.7 12 21 12 21z" />
              </svg>
            </button>
            <button type="button" class="inline-flex items-center gap-2 text-steel-400 hover:text-white" aria-label="Comments">
              <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <button type="button" class="inline-flex items-center gap-2" @click.stop.prevent="sharePost(post)">
              <svg class="h-6 w-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M12 3v13" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M8 7l4-4 4 4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <div class="ml-auto">
              <button type="button" class="p-1 text-steel-400 hover:text-white">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </div>
          </div>

          <p class="mt-2 text-sm font-semibold text-white">{{ post.likeCount }} likes</p>

          <p class="mt-1 text-sm text-steel-200">
            <span class="font-semibold text-white">@{{ post.authorHandle }}</span>
            <span class="ml-1">{{ post.caption }}</span>
          </p>

          <p v-if="post.commentCount" class="mt-2 text-xs text-steel-400">View all {{ post.commentCount }} comments</p>

          <p class="mt-2 text-xs text-steel-400">{{ post.createdLabel }}</p>
        </div>
      </router-link>
    </div>

    <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
      {{ errorMessage }}
    </p>

    <div
      v-if="showReportModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6"
      @click.self="closeReportModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between">
          <h3 class="font-display text-lg font-semibold text-white">Report post</h3>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeReportModal"
          >
            Close
          </button>
        </div>
        <p class="mt-3 text-xs text-steel-400">Tell us what is wrong with this post.</p>
        <textarea
          v-model.trim="reportReason"
          rows="4"
          maxlength="500"
          placeholder="Reason (e.g. nude content, spam, harassment)"
          class="mt-3 w-full resize-none rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-rose-400/70 focus:outline-none focus:ring-2 focus:ring-rose-400/30"
        ></textarea>
        <div class="mt-3 flex items-center justify-between">
          <p v-if="reportError" class="text-xs text-rose-300">{{ reportError }}</p>
          <p v-if="reportStatus" class="text-xs text-neon-300">{{ reportStatus }}</p>
          <button
            type="button"
            class="ml-auto rounded-full bg-white px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-rose-300 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="reportSubmitting || !reportReason"
            @click="submitReport"
          >
            {{ reportSubmitting ? 'Sending...' : 'Send report' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showLikesModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6"
      @click.self="closeLikesModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between">
          <h3 class="font-display text-lg font-semibold text-white">Likes</h3>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeLikesModal"
          >
            Close
          </button>
        </div>
        <p v-if="likesLoading" class="mt-4 text-sm text-steel-300">Loading...</p>
        <p v-else-if="likesError" class="mt-4 text-sm text-rose-200">{{ likesError }}</p>
        <p v-else-if="!likesUsers.length" class="mt-4 text-sm text-steel-300">No likes yet.</p>
        <div v-else class="mt-4 space-y-3">
          <router-link
            v-for="user in likesUsers"
            :key="user.id"
            :to="`/u/${user.handle}`"
            class="flex items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/60 px-3 py-2 hover:border-white/20"
            @click="closeLikesModal"
          >
            <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="user.avatar"
                :src="user.avatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                {{ user.initials }}
              </div>
            </div>
            <div class="min-w-0">
              <p class="truncate text-sm text-white">{{ user.fullName }}</p>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- New Post floating button -->
    <button
      type="button"
      class="fixed z-50 right-6 bottom-6 inline-flex items-center justify-center gap-2 rounded-full sm:rounded-2xl bg-white text-midnight-900 h-14 w-14 p-0 sm:px-4 sm:py-2 sm:h-auto sm:w-auto shadow-lg transition hover:bg-neon-400"
      @click="openNewPostModal"
      aria-label="New post"
    >
      <span class="sm:hidden text-lg font-bold">+</span>
      <span class="hidden sm:inline-flex items-center gap-2">
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        New Post
      </span>
    </button>

    <div
      v-if="showNewPostModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6"
      @click.self="closeNewPostModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow overflow-auto max-h-[90vh]">
        <div class="flex items-center justify-between">
          <h3 class="font-display text-lg font-semibold text-white">New post</h3>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeNewPostModal"
          >
            Close
          </button>
        </div>
        <p v-if="statusMessage" class="mt-4 text-sm text-steel-300">{{ statusMessage }}</p>
        <p v-if="isPosting" class="mt-4 text-sm text-steel-300">Posting...</p>
        <p v-if="errorMessage" class="mt-4 text-sm text-rose-200">{{ errorMessage }}</p>
        <div class="mt-4 flex flex-col gap-3">
          <input v-model.trim="newPostCaption" type="text" placeholder="Caption" class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
          <textarea v-model.trim="newPostDescription" rows="3" placeholder="Description" class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"></textarea>
          <textarea v-model.trim="newPostModifications" rows="3" placeholder="Car modifications" class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"></textarea>
          <input v-model.trim="newPostEngine" type="text" placeholder="Engine" class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
          <div class="grid gap-3 sm:grid-cols-3">
            <input v-model.number="newPostHpStock" type="number" min="0" placeholder="HP Stock" class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
            <input v-model.number="newPostHpTuned" type="number" min="0" placeholder="HP After Tuned" class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
            <input v-model.number="newPostYear" type="number" min="1900" max="2100" placeholder="Year" class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30" />
          </div>
          <label class="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-steel-300">
            <input v-model="newPostForSale" type="checkbox" class="h-4 w-4 rounded border-white/20 bg-midnight-900/60 text-neon-400 focus:ring-neon-400/40" />
            For sale
          </label>
          <label class="inline-flex cursor-pointer items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:border-white/30 hover:bg-white/10">
            <input
              class="absolute left-0 top-0 h-0 w-0 opacity-0"
              type="file"
              accept="image/*"
              multiple
              @change="handlePostUpload"
            />
            Upload photos (max 5)
          </label>
          <p v-if="newPostPreviews.length" class="text-xs text-steel-300">{{ newPostPreviews.length }} photo(s) ready.</p>
          <div v-if="newPostPreviews.length" class="rounded-2xl border border-white/10 bg-midnight-900/60 p-3">
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Adjust framing</p>
            <p class="mt-2 text-xs text-steel-400">Drag each photo to position it inside the post crop.</p>
            <div class="mt-3 grid gap-3 sm:grid-cols-2">
              <div
                v-for="(url, idx) in newPostPreviews"
                :key="url + idx"
                class="relative aspect-square w-full overflow-hidden rounded-2xl border border-white/10 bg-black"
                style="touch-action: none;"
                @pointerdown="startPreviewDrag(idx, $event)"
              >
                <img
                  :src="url"
                  alt="Preview"
                  class="h-full w-full select-none object-cover"
                  :style="{ objectPosition: previewPosition(idx) }"
                  draggable="false"
                />
                <div class="pointer-events-none absolute inset-x-0 bottom-2 text-center text-[10px] uppercase tracking-[0.3em] text-white/70">
                  Drag to move
                </div>
              </div>
            </div>
          </div>
          <button type="button" class="rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70" :disabled="isPosting || !isPostValid" @click="publishPost">
            {{ isPosting ? 'Posting...' : 'Publish post' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Floating toast notification (visible on all screen sizes) -->
    <div v-if="statusMessage" class="fixed bottom-6 left-1/2 z-60 -translate-x-1/2 rounded-full bg-neon-400/95 px-4 py-2 text-sm font-semibold text-midnight-900 shadow pointer-events-none">
      {{ statusMessage }}
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl } from '@/lib/supabase'

export default {
  name: 'FeedView',
  data() {
    return {
      posts: [],
      errorMessage: '',
      statusMessage: '',
      userId: '',
      showLikesModal: false,
      likesLoading: false,
      likesError: '',
      likesUsers: [],
      activeFeedFilter: 'feed',
      showNewPostModal: false,
      newPostCaption: '',
      newPostDescription: '',
      newPostModifications: '',
      newPostEngine: '',
      newPostHpStock: null,
      newPostHpTuned: null,
      newPostYear: null,
      newPostForSale: false,
      newPostPreviews: [],
      newPostPaths: [],
      newPostOffsets: [],
      previewDrag: null,
      isPosting: false,
      topUsers: [],
      topUsersLoading: false,
      showReportModal: false,
      reportPost: null,
      reportReason: '',
      reportSubmitting: false,
      reportError: '',
      reportStatus: '',
      swipeStartX: 0,
      swipeEndX: 0,
      swipeStartY: 0,
      swipeEndY: 0,
      preloadedImages: {},
    }
  },
  computed: {
    filteredPosts() {
      if (this.activeFeedFilter === 'selling') {
        return this.posts.filter((post) => post.is_for_sale)
      }
      return this.posts.filter((post) => !post.is_for_sale)
    },
    isPostValid() {
      // Only photos are required to publish
      return this.newPostPreviews.length > 0
    },
  },
  async mounted() {
    const { data } = await supabase.auth.getSession()
    this.userId = data?.session?.user?.id || ''
    await this.fetchPosts()
    await this.fetchTopUsers()
  },
  methods: {
    openReportModal(post) {
      this.reportPost = post
      this.reportReason = ''
      this.reportError = ''
      this.reportStatus = ''
      this.showReportModal = true
    },
    closeReportModal() {
      this.showReportModal = false
      this.reportPost = null
      this.reportReason = ''
      this.reportError = ''
      this.reportStatus = ''
      this.reportSubmitting = false
    },
    async submitReport() {
      if (!this.userId) {
        this.reportError = 'Please sign in to report posts.'
        return
      }
      if (!this.reportPost?.id) {
        this.reportError = 'Post not found.'
        return
      }

      const reason = this.reportReason.trim()
      if (!reason) return

      this.reportSubmitting = true
      this.reportError = ''
      this.reportStatus = ''

      const { error } = await supabase.from('post_reports').insert({
        post_id: this.reportPost.id,
        reporter_id: this.userId,
        reason,
      })

      this.reportSubmitting = false

      if (error) {
        this.reportError = error.message || 'Unable to submit report.'
        return
      }

      this.reportStatus = 'Report sent. Thank you.'
      setTimeout(() => this.closeReportModal(), 1200)
    },
    async fetchPosts() {
      this.errorMessage = ''

      const { data, error } = await supabase
        .from('posts')
        .select(
          'id, caption, description, modifications, engine, hp_stock, hp_tuned, year, is_for_sale, image_url, image_path, image_offset_x, image_offset_y, created_at, user_id, profiles (handle, full_name, avatar_url, avatar_path), post_media (image_url, image_path, sort_order, offset_x, offset_y)'
        )
        .order('created_at', { ascending: false })

      if (error) {
        this.errorMessage = error.message || 'Unable to load feed yet.'
        return
      }

      const posts = (data || []).map((post) => {
        const profile = post.profiles || {}
        const authorAvatar = getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url })

        const mediaItems = [
          {
            url: post.image_path
              ? supabase.storage.from('posts').getPublicUrl(post.image_path).data?.publicUrl
              : post.image_url,
            offset: {
              x: Number.isFinite(post.image_offset_x) ? post.image_offset_x : 50,
              y: Number.isFinite(post.image_offset_y) ? post.image_offset_y : 50,
            },
          },
          ...(post.post_media || []).map((item) => ({
            url: item.image_path
              ? supabase.storage.from('posts').getPublicUrl(item.image_path).data?.publicUrl
              : item.image_url,
            offset: {
              x: Number.isFinite(item.offset_x) ? item.offset_x : 50,
              y: Number.isFinite(item.offset_y) ? item.offset_y : 50,
            },
          })),
        ].filter((item) => Boolean(item.url))

        const mediaUrls = mediaItems.map((item) => item.url)
        const mediaOffsets = mediaItems.map((item) => item.offset)

        const normalized = {
          ...post,
          authorName: profile.full_name || 'DRVN Member',
          authorHandle: profile.handle || 'member',
          authorAvatar,
          authorInitials: (profile.full_name || 'D')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((chunk) => chunk[0].toUpperCase())
            .join(''),
          createdLabel: new Date(post.created_at).toLocaleDateString(),
          mediaUrls,
          mediaOffsets,
          mediaIndex: 0,
          mediaDirection: 'next',
          likeCount: 0,
          commentCount: 0,
          isLiked: false,
          newComment: '',
        }

        this.preloadMedia(normalized.mediaUrls, normalized.mediaIndex || 0)

        return normalized
      })

      const postIds = posts.map((post) => post.id)

      if (postIds.length > 0) {
        const { data: likes } = await supabase
          .from('post_likes')
          .select('post_id, user_id')
          .in('post_id', postIds)

        const { data: comments } = await supabase
          .from('post_comments')
          .select('post_id')
          .in('post_id', postIds)

        posts.forEach((post) => {
          post.likeCount = likes?.filter((like) => like.post_id === post.id).length || 0
          post.commentCount = comments?.filter((comment) => comment.post_id === post.id).length || 0
          post.isLiked = Boolean(likes?.some((like) => like.post_id === post.id && like.user_id === this.userId))
        })
      }

      this.posts = posts

      // Also refresh top users since feed changed
      await this.fetchTopUsers()
    },
    currentMedia(post) {
      if (!post.mediaUrls || post.mediaUrls.length === 0) return ''
      const index = post.mediaIndex || 0
      return post.mediaUrls[index]
    },
    currentMediaPosition(post) {
      if (!post.mediaOffsets || post.mediaOffsets.length === 0) return '50% 50%'
      const index = post.mediaIndex || 0
      const offset = post.mediaOffsets[index] || { x: 50, y: 50 }
      return `${offset.x}% ${offset.y}%`
    },
    previewPosition(idx) {
      const offset = this.newPostOffsets[idx] || { x: 50, y: 50 }
      return `${offset.x}% ${offset.y}%`
    },
    startPreviewDrag(idx, event) {
      event.preventDefault()
      if (!this.newPostOffsets[idx]) {
        this.newPostOffsets[idx] = { x: 50, y: 50 }
      }
      const target = event.currentTarget
      if (!target) return
      if (typeof target.setPointerCapture === 'function') {
        try {
          target.setPointerCapture(event.pointerId)
        } catch (e) {
          // ignore if capture fails
        }
      }
      const rect = target.getBoundingClientRect()
      const startOffset = this.newPostOffsets[idx]
      this.previewDrag = {
        idx,
        startX: event.clientX,
        startY: event.clientY,
        startOffsetX: startOffset.x,
        startOffsetY: startOffset.y,
        width: rect.width || 1,
        height: rect.height || 1,
      }
      const onMove = (moveEvent) => {
        if (!this.previewDrag) return
        moveEvent.preventDefault()
        const dx = moveEvent.clientX - this.previewDrag.startX
        const dy = moveEvent.clientY - this.previewDrag.startY
        const nextX = this.previewDrag.startOffsetX + (dx / this.previewDrag.width) * 100
        const nextY = this.previewDrag.startOffsetY + (dy / this.previewDrag.height) * 100
        this.newPostOffsets[this.previewDrag.idx] = {
          x: Math.max(0, Math.min(100, Math.round(nextX))),
          y: Math.max(0, Math.min(100, Math.round(nextY))),
        }
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        this.previewDrag = null
      }
      window.addEventListener('pointermove', onMove, { passive: false })
      window.addEventListener('pointerup', onUp)
    },
    preloadMedia(urls, index = 0) {
      if (!urls || !urls.length) return
      const candidates = [index, index + 1, index - 1]
        .filter((i) => i >= 0 && i < urls.length)
        .map((i) => urls[i])
        .filter(Boolean)

      candidates.forEach((url) => {
        if (this.preloadedImages[url]) return
        const img = new Image()
        img.decoding = 'async'
        img.src = url
        this.preloadedImages[url] = true
      })
    },
    setMediaIndex(post, idx) {
      if (!post?.mediaUrls || idx < 0 || idx >= post.mediaUrls.length) return
      post.mediaDirection = idx > (post.mediaIndex || 0) ? 'next' : 'prev'
      post.mediaIndex = idx
      this.preloadMedia(post.mediaUrls, post.mediaIndex || 0)
    },
    handleSwipeStart(e) {
      this.swipeStartX = e.changedTouches[0].clientX
      this.swipeStartY = e.changedTouches[0].clientY
    },
    handleSwipeEnd(e, post) {
      this.swipeEndX = e.changedTouches[0].clientX
      this.swipeEndY = e.changedTouches[0].clientY
      if (!post.mediaUrls || post.mediaUrls.length <= 1) return
      
      const swipeThreshold = 50
      const diffX = this.swipeStartX - this.swipeEndX
      const diffY = Math.abs(this.swipeStartY - this.swipeEndY)
      
      // Only navigate carousel if horizontal movement is greater than vertical movement
      if (Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > diffY) {
        if (diffX > 0) {
          this.navigateToNext(post)
        } else {
          this.navigateToPrevious(post)
        }
      }
    },
    navigateToNext(post) {
      if (!post.mediaUrls || post.mediaUrls.length <= 1) return
      post.mediaDirection = 'next'
      post.mediaIndex = (post.mediaIndex || 0) + 1
      if (post.mediaIndex >= post.mediaUrls.length) {
        post.mediaIndex = 0
      }
      this.preloadMedia(post.mediaUrls, post.mediaIndex || 0)
      this.$forceUpdate()
    },
    navigateToPrevious(post) {
      if (!post.mediaUrls || post.mediaUrls.length <= 1) return
      post.mediaDirection = 'prev'
      post.mediaIndex = (post.mediaIndex || 1) - 1
      if (post.mediaIndex < 0) {
        post.mediaIndex = post.mediaUrls.length - 1
      }
      this.preloadMedia(post.mediaUrls, post.mediaIndex || 0)
      this.$forceUpdate()
    },
    async toggleLike(post) {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to like posts.'
        return
      }

      if (post.isLiked) {
        await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', post.id)
          .eq('user_id', this.userId)
        post.isLiked = false
        post.likeCount = Math.max(0, post.likeCount - 1)
      } else {
        await supabase.from('post_likes').insert({ post_id: post.id, user_id: this.userId })
        post.isLiked = true
        post.likeCount += 1
        await this.notifyPostOwner(post, 'post_like')
      }

      // refresh the top users list since like counts changed
      await this.fetchTopUsers()
    },
    async addComment(post) {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to comment.'
        return
      }

      if (!post.newComment) return

      const { error } = await supabase.from('post_comments').insert({
        post_id: post.id,
        user_id: this.userId,
        content: post.newComment,
      })

      if (error) {
        this.errorMessage = error.message
        return
      }

      post.commentCount += 1
      post.newComment = ''
      await this.notifyPostOwner(post, 'post_comment')
    },
    async notifyPostOwner(post, type) {
      if (!post?.user_id || post.user_id === this.userId) return
      await supabase.from('notifications').insert({
        recipient_id: post.user_id,
        actor_id: this.userId,
        post_id: post.id,
        type,
      })
    },
    async sharePost(post) {
      this.errorMessage = ''
      this.statusMessage = ''
      const postUrl = `${window.location.origin}/post/${post.id}`

      const ok = await this.copyTextToClipboard(postUrl)
      if (ok) {
        this.statusMessage = 'Post link copied to clipboard.'
      } else {
        this.errorMessage = 'Unable to copy link. ' + postUrl
      }

      setTimeout(() => (this.statusMessage = ''), 3000)
    },

    async copyTextToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text)
          return true
        } catch (e) {
          // fall through to legacy fallback
        }
      }

      try {
        const el = document.createElement('textarea')
        el.value = text
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)

        const selection = document.getSelection()
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null
        el.select()
        const success = document.execCommand('copy')
        document.body.removeChild(el)

        if (range && selection) {
          selection.removeAllRanges()
          selection.addRange(range)
        }

        return success
      } catch (e) {
        return false
      }
    },
    closeLikesModal() {
      this.showLikesModal = false
      this.likesUsers = []
      this.likesError = ''
      this.likesLoading = false
    },
    async openLikesModal(post) {
      this.showLikesModal = true
      this.likesLoading = true
      this.likesError = ''
      this.likesUsers = []

      const { data: likes, error } = await supabase
        .from('post_likes')
        .select('user_id')
        .eq('post_id', post.id)

      if (error) {
        this.likesError = 'Unable to load likes.'
        this.likesLoading = false
        return
      }

      const userIds = (likes || []).map((like) => like.user_id).filter(Boolean)
      if (!userIds.length) {
        this.likesUsers = []
        this.likesLoading = false
        return
      }

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, handle, full_name, avatar_url, avatar_path')
        .in('id', userIds)

      if (profileError) {
        this.likesError = 'Unable to load likes.'
        this.likesLoading = false
        return
      }

      const profileMap = new Map((profiles || []).map((profile) => [profile.id, profile]))

      this.likesUsers = userIds.map((userId) => {
        const profile = profileMap.get(userId) || {}
        const avatar = profile.avatar_path
          ? supabase.storage.from('avatars').getPublicUrl(profile.avatar_path).data?.publicUrl
          : profile.avatar_url || ''
        const fullName = profile.full_name || 'DRVN Member'
        const handle = profile.handle || 'member'
        const initials = fullName
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((chunk) => chunk[0].toUpperCase())
          .join('')
        return {
          id: userId,
          handle,
          fullName,
          avatar,
          initials,
        }
      })
      this.likesLoading = false
    },
    openNewPostModal() {
      this.showNewPostModal = true
      this.statusMessage = ''
      this.errorMessage = ''
    },
    closeNewPostModal() {
      this.showNewPostModal = false
      this.statusMessage = ''
      this.errorMessage = ''
      this.newPostCaption = ''
      this.newPostDescription = ''
      this.newPostModifications = ''
      this.newPostEngine = ''
      this.newPostHpStock = null
      this.newPostHpTuned = null
      this.newPostYear = null
      this.newPostForSale = false
      this.newPostPreviews = []
      this.newPostPaths = []
      this.newPostOffsets = []
      this.newPostOffsets = []
      this.isPosting = false
    },
    async convertImageToWebp(file, { maxWidth, quality = 0.82 }) {
      const image = await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Image decode failed.'))
        img.src = URL.createObjectURL(file)
      })

      const originalWidth = image.width || 1
      const originalHeight = image.height || 1
      const scale = Math.min(1, maxWidth / originalWidth)
      const targetWidth = Math.max(1, Math.round(originalWidth * scale))
      const targetHeight = Math.max(1, Math.round(originalHeight * scale))

      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = targetHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(image, 0, 0, targetWidth, targetHeight)

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
      if (!blob) throw new Error('Image conversion failed.')

      return new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' })
    },
    async handlePostUpload(event) {
      const files = Array.from(event.target.files || [])
      if (!files.length) return

      if (files.length > 5) {
        this.errorMessage = 'You can upload up to 5 photos.'
        return
      }

      this.errorMessage = ''
      this.statusMessage = ''

      const userId = this.userId
      if (!userId) {
        this.errorMessage = 'Please sign in to upload a post.'
        return
      }

      this.newPostPreviews = []
      this.newPostPaths = []

      for (const file of files) {
        let uploadFile = file
        try {
          uploadFile = await this.convertImageToWebp(file, { maxWidth: 1600 })
        } catch (error) {
          this.errorMessage = 'One of the photos could not be processed.'
          return
        }

        const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '').replace(/\.[^.]+$/, '.webp')
        const filePath = `${userId}/${Date.now()}-${safeName}`

        const { error: uploadError } = await supabase.storage
          .from('posts')
          .upload(filePath, uploadFile, { upsert: false, contentType: 'image/webp' })

        if (uploadError) {
          this.errorMessage = 'Post upload failed. Make sure the "posts" bucket exists.'
          return
        }

        const { data: publicUrl } = supabase.storage
          .from('posts')
          .getPublicUrl(filePath)

        this.newPostPaths.push(filePath)
        this.newPostPreviews.push(publicUrl?.publicUrl || '')
        this.newPostOffsets.push({ x: 50, y: 50 })
      }

      this.statusMessage = 'Photos uploaded. Publish when ready.'
      if (event?.target) {
        event.target.value = ''
      }
    },
    async publishPost() {
      if (!this.isPostValid) {
        this.errorMessage = 'Please add at least one photo to publish.'
        return
      }

      this.isPosting = true
      this.errorMessage = ''
      this.statusMessage = ''

      const userId = this.userId
      if (!userId) {
        this.errorMessage = 'Please sign in to publish.'
        this.isPosting = false
        return
      }

      const { data: postRow, error } = await supabase
        .from('posts')
        .insert({
          user_id: userId,
          caption: this.newPostCaption,
          description: this.newPostDescription,
          modifications: this.newPostModifications,
          engine: this.newPostEngine,
          hp_stock: this.newPostHpStock,
          hp_tuned: this.newPostHpTuned,
          year: this.newPostYear,
          is_for_sale: this.newPostForSale,
          image_url: this.newPostPreviews[0],
          image_path: this.newPostPaths[0],
          image_offset_x: this.newPostOffsets[0]?.x ?? 50,
          image_offset_y: this.newPostOffsets[0]?.y ?? 50,
        })
        .select('id')
        .single()

      this.isPosting = false

      if (error) {
        this.errorMessage = error.message
        return
      }

      if (postRow?.id && this.newPostPaths.length > 1) {
        const mediaRows = this.newPostPaths.slice(1).map((path, index) => ({
          post_id: postRow.id,
          image_path: path,
          image_url: this.newPostPreviews[index + 1],
          sort_order: index + 1,
          offset_x: this.newPostOffsets[index + 1]?.x ?? 50,
          offset_y: this.newPostOffsets[index + 1]?.y ?? 50,
        }))
        await supabase.from('post_media').insert(mediaRows)
      }

      // reset and refresh
      this.closeNewPostModal()
      this.statusMessage = 'Post published.'
      await this.fetchPosts()
    },

    async fetchTopUsers() {
      this.topUsersLoading = true
      try {
        // fetch posts with their likes aggregated per post
        const { data: posts, error } = await supabase.from('posts').select('user_id, post_likes (id)')
        if (error) {
          this.topUsers = []
          this.topUsersLoading = false
          return
        }

        const likeMap = new Map()
        ;(posts || []).forEach((p) => {
          const userId = p.user_id
          const count = (p.post_likes || []).length || 0
          likeMap.set(userId, (likeMap.get(userId) || 0) + count)
        })

        const usersSorted = Array.from(likeMap.entries())
          .map(([userId, count]) => ({ userId, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5)

        if (!usersSorted.length) {
          this.topUsers = []
          this.topUsersLoading = false
          return
        }

        const userIds = usersSorted.map((u) => u.userId)
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, handle, full_name, avatar_url, avatar_path')
          .in('id', userIds)

        const profileMap = new Map((profiles || []).map((p) => [p.id, p]))

        this.topUsers = usersSorted.map((u, idx) => {
          const profile = profileMap.get(u.userId) || {}
          const avatar = getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url })
          const fullName = profile.full_name || 'DRVN Member'
          const initials = fullName
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((c) => c[0].toUpperCase())
            .join('')
          return {
            id: u.userId,
            handle: profile.handle || 'member',
            fullName,
            avatar,
            initials,
            likeCount: u.count,
            rank: idx + 1,
          }
        })
      } catch (e) {
        this.topUsers = []
      }

      this.topUsersLoading = false
    }
  },
}
</script>

<style>
/* hide horizontal scrollbar for the Top Members scroller across browsers */
.no-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
.no-scrollbar::-webkit-scrollbar {
  height: 0;
  display: none; /* Safari and Chrome */
}

.media-swipe-next-enter-active,
.media-swipe-next-leave-active,
.media-swipe-prev-enter-active,
.media-swipe-prev-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.media-swipe-next-enter-from {
  opacity: 0;
  transform: translateX(10%);
}

.media-swipe-next-leave-to {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-enter-from {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-leave-to {
  opacity: 0;
  transform: translateX(10%);
}
</style>
